import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

const emptyExam = {
  course_id: '',
  title: '',
  description: '',
  instructions: '',
  duration_minutes: '',
  attempt_policy: 'single',
  max_attempts: '',
  pass_percentage: 40,
  negative_marking_enabled: false,
  shuffle_questions: true,
  shuffle_options: true,
  reveal_policy: 'immediate',
  show_correct_answers: true,
  available_from: '',
  available_until: '',
};

function SortableBankRow({ row, onCountChange, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.bank_id.toString() });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="d-flex align-items-center gap-16 p-12 mb-8" >
      <span {...attributes} {...listeners} style={{ cursor: 'grab' }}><i className="ph ph-dots-six-vertical text-gray-500" /></span>
      <div className="flex-grow-1">
        <div className="fw-semibold">{row.bank_name}</div>
        <div className="text-13 text-gray-400">{row.active_questions ?? '—'} active questions available</div>
      </div>
      <div style={{ width: 140 }}>
        <input
          type="number" min="1" className="form-control py-9" placeholder="Question count"
          value={row.question_count} onChange={(e) => onCountChange(row.bank_id, e.target.value)}
        />
      </div>
      <button type="button" className="btn btn-sm py-4 px-8" style={{ backgroundColor: '#dc3545', borderColor: '#dc3545', color: '#fff', opacity: 1 }} onClick={() => onRemove(row.bank_id)}>
        <i className="ph ph-x" />
      </button>
    </div>
  );
}

function AdminMockExamForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [activeTab, setActiveTab] = useState('details');
  const [form, setForm] = useState(emptyExam);
  const [courses, setCourses] = useState([]);
  const [availableBanks, setAvailableBanks] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState('');
  const [examBanks, setExamBanks] = useState([]); // [{bank_id, bank_name, question_count, active_questions}]
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const getToken = () => {
    const session = JSON.parse(sessionStorage.getItem('adminSession'));
    return session?.token || null;
  };

  const fetchCourses = useCallback(async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/admin/courses`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) setCourses(response.data.courses || []);
    } catch (err) { /* non-fatal */ }
  }, []);

  const fetchBanksForCourse = useCallback(async (courseId) => {
    if (!courseId) { setAvailableBanks([]); return; }
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/admin/mock-exams/banks`, { headers: { Authorization: `Bearer ${token}` }, params: { courseId, status: 'active' } });
      if (response.data.success) setAvailableBanks(response.data.banks || []);
    } catch (err) { /* non-fatal */ }
  }, []);

  const fetchExam = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/admin/mock-exams/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) {
        const e = response.data.exam;
        setForm({
          course_id: e.course_id,
          title: e.title,
          description: e.description || '',
          instructions: e.instructions || '',
          duration_minutes: e.duration_minutes || '',
          attempt_policy: e.attempt_policy,
          max_attempts: e.max_attempts || '',
          pass_percentage: e.pass_percentage,
          negative_marking_enabled: !!e.negative_marking_enabled,
          shuffle_questions: !!e.shuffle_questions,
          shuffle_options: !!e.shuffle_options,
          reveal_policy: e.reveal_policy,
          show_correct_answers: !!e.show_correct_answers,
          available_from: e.available_from ? e.available_from.slice(0, 16) : '',
          available_until: e.available_until ? e.available_until.slice(0, 16) : '',
        });
        setExamBanks((response.data.banks || []).map((b) => ({
          bank_id: b.bank_id, bank_name: b.bank_name, question_count: b.question_count, active_questions: b.active_questions,
        })));
        fetchBanksForCourse(e.course_id);
      } else {
        setError(response.data.message || 'Failed to fetch exam.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching exam.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);
  useEffect(() => { fetchExam(); }, [fetchExam]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleCourseChange = (courseId) => {
    setField('course_id', courseId);
    setExamBanks([]);
    fetchBanksForCourse(courseId);
  };

  const addBank = () => {
    if (!selectedBankId) return;
    if (examBanks.some((b) => String(b.bank_id) === String(selectedBankId))) return;
    const bank = availableBanks.find((b) => String(b.id) === String(selectedBankId));
    if (!bank) return;
    setExamBanks([...examBanks, { bank_id: bank.id, bank_name: bank.name, question_count: 5, active_questions: bank.active_questions }]);
    setSelectedBankId('');
  };

  const removeBank = (bankId) => setExamBanks(examBanks.filter((b) => String(b.bank_id) !== String(bankId)));
  const updateBankCount = (bankId, count) => setExamBanks(examBanks.map((b) => (String(b.bank_id) === String(bankId) ? { ...b, question_count: count } : b)));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setExamBanks((items) => {
        const oldIndex = items.findIndex((i) => i.bank_id.toString() === active.id);
        const newIndex = items.findIndex((i) => i.bank_id.toString() === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const buildPayload = () => ({
    course_id: form.course_id,
    title: form.title,
    description: form.description,
    instructions: form.instructions,
    duration_minutes: form.duration_minutes || null,
    attempt_policy: form.attempt_policy,
    max_attempts: form.attempt_policy === 'limited' ? form.max_attempts : null,
    pass_percentage: form.pass_percentage,
    negative_marking_enabled: form.negative_marking_enabled,
    shuffle_questions: form.shuffle_questions,
    shuffle_options: form.shuffle_options,
    reveal_policy: form.reveal_policy,
    show_correct_answers: form.show_correct_answers,
    available_from: form.available_from || null,
    available_until: form.available_until || null,
    banks: examBanks.map((b) => ({ bank_id: b.bank_id, question_count: Number(b.question_count) || 0 })),
  });

  const handleSave = async () => {
    setError('');
    setSuccess('');
    if (!form.course_id || !form.title.trim()) {
      setError('Course and title are required.');
      setActiveTab('details');
      return;
    }
    setSaving(true);
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const payload = buildPayload();
      let examId = id;
      if (!isEdit) {
        const createResponse = await axios.post(`${apiUrl}/api/admin/mock-exams`, payload, { headers });
        if (!createResponse.data.success) throw new Error(createResponse.data.message);
        examId = createResponse.data.id;
      }
      const updateResponse = await axios.put(`${apiUrl}/api/admin/mock-exams/${examId}`, payload, { headers });
      if (updateResponse.data.success) {
        setSuccess('Exam saved successfully.');
        if (!isEdit) {
          setTimeout(() => navigate(`/admin-mock-exam-form/${examId}`), 800);
        } else if (updateResponse.data.revertedToDraft) {
          setSuccess('Exam saved. Since it was published, it has been reverted to Draft — republish once you\'re ready.');
        }
      } else {
        setError(updateResponse.data.message || 'Failed to save exam.');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error saving exam.');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setError('');
    setSuccess('');
    try {
      const token = getToken();
      const response = await axios.put(`${apiUrl}/api/admin/mock-exams/${id}/publish`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) {
        setSuccess('Exam published successfully.');
      } else {
        setError((response.data.errors || [response.data.message]).join(' '));
      }
    } catch (err) {
      const errors = err.response?.data?.errors;
      setError(errors ? errors.join(' ') : (err.response?.data?.message || 'Error publishing exam.'));
    }
  };

  if (loading) {
    return (
      <>
        <SideBar />
        <div className="dashboard-main-wrapper">
          <TopNavBar />
          <div className="dashboard-body text-center py-40"><div className="spinner-border text-main-600" /></div>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><Link to="/admin-mock-exams" className="text-gray-200 fw-normal text-15 hover-text-main-600">Mock Exams</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">{isEdit ? 'Edit Exam' : 'Create Exam'}</span></li>
              </ul>
            </div>
            {isEdit && (
              <button type="button" className="btn rounded-pill py-9" style={{ backgroundColor: '#198754', borderColor: '#198754', color: '#fff', opacity: 1 }} onClick={handlePublish}>
                <i className="ph ph-rocket-launch me-2" /> Publish
              </button>
            )}
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          <ul className="nav nav-tabs mb-20">
            <li className="nav-item">
              <button type="button" className={`nav-link ${activeTab === 'details' ? 'active' : ''}`} onClick={() => setActiveTab('details')}>Details</button>
            </li>
            <li className="nav-item">
              <button type="button" className={`nav-link ${activeTab === 'banks' ? 'active' : ''}`} onClick={() => setActiveTab('banks')}>Question Banks</button>
            </li>
          </ul>

          {activeTab === 'details' && (
            <div className="card mb-24">
              <div className="card-body">
                <div className="row g-20">
                  <div className="col-sm-6">
                    <label className="h5 mb-8 fw-semibold font-heading">Course <span className="text-red-500">*</span></label>
                    <select className="form-control py-11" value={form.course_id} onChange={(e) => handleCourseChange(e.target.value)} disabled={saving}>
                      <option value="">Select course...</option>
                      {courses.map((c) => (<option key={c.id} value={c.id}>{c.coursename}</option>))}
                    </select>
                  </div>
                  <div className="col-sm-6">
                    <label className="h5 mb-8 fw-semibold font-heading">Exam Title <span className="text-red-500">*</span></label>
                    <input type="text" className="form-control py-11" value={form.title} onChange={(e) => setField('title', e.target.value)} disabled={saving} />
                  </div>

                  <div className="col-12">
                    <label className="h5 mb-8 fw-semibold font-heading d-block">Description</label>
                    <CKEditor editor={ClassicEditor} data={form.description} onChange={(evt, editor) => setField('description', editor.getData())}
                      config={{ toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|', 'undo', 'redo'] }} />
                  </div>
                  <div className="col-12">
                    <label className="h5 mb-8 fw-semibold font-heading d-block">Instructions (shown before exam starts)</label>
                    <CKEditor editor={ClassicEditor} data={form.instructions} onChange={(evt, editor) => setField('instructions', editor.getData())}
                      config={{ toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|', 'undo', 'redo'] }} />
                  </div>

                  <div className="col-sm-4">
                    <label className="h5 mb-8 fw-semibold font-heading">Duration (minutes)</label>
                    <input type="number" min="1" className="form-control py-11" placeholder="Leave blank = untimed"
                      value={form.duration_minutes} onChange={(e) => setField('duration_minutes', e.target.value)} disabled={saving} />
                  </div>
                  <div className="col-sm-4">
                    <label className="h5 mb-8 fw-semibold font-heading">Pass Percentage <span className="text-red-500">*</span></label>
                    <input type="number" min="0" max="100" className="form-control py-11" value={form.pass_percentage}
                      onChange={(e) => setField('pass_percentage', e.target.value)} disabled={saving} />
                  </div>
                  <div className="col-sm-4">
                    <label className="d-flex align-items-center gap-8 mt-32">
                      <input type="checkbox" checked={form.negative_marking_enabled} onChange={(e) => setField('negative_marking_enabled', e.target.checked)} disabled={saving} />
                      <span>Enable negative marking</span>
                    </label>
                  </div>

                  <div className="col-sm-4">
                    <label className="h5 mb-8 fw-semibold font-heading">Attempt Policy</label>
                    <select className="form-control py-11" value={form.attempt_policy} onChange={(e) => setField('attempt_policy', e.target.value)} disabled={saving}>
                      <option value="single">One-Time Attempt</option>
                      <option value="limited">Limited Attempts</option>
                      <option value="unlimited">Unlimited Attempts</option>
                    </select>
                  </div>
                  {form.attempt_policy === 'limited' && (
                    <div className="col-sm-4">
                      <label className="h5 mb-8 fw-semibold font-heading">Maximum Attempts <span className="text-red-500">*</span></label>
                      <input type="number" min="1" className="form-control py-11" value={form.max_attempts} onChange={(e) => setField('max_attempts', e.target.value)} disabled={saving} />
                    </div>
                  )}

                  <div className="col-sm-4">
                    <label className="d-flex align-items-center gap-8 mt-32">
                      <input type="checkbox" checked={form.shuffle_questions} onChange={(e) => setField('shuffle_questions', e.target.checked)} disabled={saving} />
                      <span>Randomize question order</span>
                    </label>
                  </div>
                  <div className="col-sm-4">
                    <label className="d-flex align-items-center gap-8">
                      <input type="checkbox" checked={form.shuffle_options} onChange={(e) => setField('shuffle_options', e.target.checked)} disabled={saving} />
                      <span>Randomize answer option order</span>
                    </label>
                  </div>

                  <div className="col-sm-4">
                    <label className="h5 mb-8 fw-semibold font-heading">Available From</label>
                    <input type="datetime-local" className="form-control py-11" value={form.available_from} onChange={(e) => setField('available_from', e.target.value)} disabled={saving} />
                  </div>
                  <div className="col-sm-4">
                    <label className="h5 mb-8 fw-semibold font-heading">Available Until</label>
                    <input type="datetime-local" className="form-control py-11" value={form.available_until} onChange={(e) => setField('available_until', e.target.value)} disabled={saving} />
                  </div>

                  <div className="col-sm-4">
                    <label className="h5 mb-8 fw-semibold font-heading">When to Reveal Correct Answers</label>
                    <select className="form-control py-11" value={form.reveal_policy} onChange={(e) => setField('reveal_policy', e.target.value)} disabled={saving}>
                      <option value="immediate">Immediately after submission</option>
                      <option value="after_close">Only after the exam closes</option>
                      <option value="manual">Never (manual release only)</option>
                    </select>
                  </div>
                  <div className="col-sm-4">
                    <label className="d-flex align-items-center gap-8 mt-32">
                      <input type="checkbox" checked={form.show_correct_answers} onChange={(e) => setField('show_correct_answers', e.target.checked)} disabled={saving} />
                      <span>Show correct answers at all (master switch)</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'banks' && (
            <div className="card mb-24">
              <div className="card-header border-bottom border-gray-100">
                <h5 className="mb-0">Question Banks for this Exam</h5>
                <span className="text-13 text-gray-400 d-block mt-4">
                  Total questions configured: {examBanks.reduce((s, b) => s + (Number(b.question_count) || 0), 0)}
                </span>
              </div>
              <div className="card-body">
                {!form.course_id ? (
                  <div className="text-gray-400 text-center py-20">Select a course in the Details tab first.</div>
                ) : (
                  <>
                    <div className="d-flex gap-8 mb-20">
                      <select className="form-control py-9" value={selectedBankId} onChange={(e) => setSelectedBankId(e.target.value)}>
                        <option value="">Select a question bank to add...</option>
                        {availableBanks.filter((b) => !examBanks.some((eb) => String(eb.bank_id) === String(b.id))).map((b) => (
                          <option key={b.id} value={b.id}>{b.name} ({b.active_questions} active)</option>
                        ))}
                      </select>
                      <button type="button" className="btn btn-main rounded-pill py-9 px-20" onClick={addBank}>Add</button>
                    </div>

                    {examBanks.length === 0 ? (
                      <div className="text-gray-400 text-center py-20">No question banks selected yet.</div>
                    ) : (
                      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={examBanks.map((b) => b.bank_id.toString())} strategy={verticalListSortingStrategy}>
                          {examBanks.map((row) => (
                            <div key={row.bank_id} style={{ background: '#f6faf8', borderRadius: 12, border: '1px solid #e8f5ee' }}>
                              <SortableBankRow row={row} onCountChange={updateBankCount} onRemove={removeBank} />
                            </div>
                          ))}
                        </SortableContext>
                      </DndContext>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          <div className="d-flex justify-content-end gap-8 mb-24">
            <button type="button" className="btn btn-outline-main rounded-pill py-9" onClick={() => navigate('/admin-mock-exams')}>Back to List</button>
            <button type="button" className="btn btn-main rounded-pill py-9" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Exam'}
            </button>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminMockExamForm;
